"use client";

import Script from "next/script";

import { CONSENT_DEFAULT_SCRIPT } from "@/shared/lib/consent";

// Google Tag Manager. Public container ID — it ships in the page source by
// design, so it isn't a secret. Overridable via NEXT_PUBLIC_GTM_ID; falls back
// to the production Hoku container. Empty string disables GTM entirely, which
// is how preview deployments and local dev stay out of the analytics data.
const GTM_ID = process.env.NEXT_PUBLIC_GTM_ID ?? "GTM-MGC7Q8ZG";

/**
 * Renders as the FIRST child of <body> in both root layouts.
 *
 * The app has two independent page shells — app/(frontend)/layout.tsx and
 * app/(blog)/layout.tsx each render their own <html>/<body> (see the comment in
 * the blog layout for why). A tag installed in one is simply absent from the
 * other, so this component goes in both. It deliberately does NOT go in
 * app/(payload)/blog-admin: the CMS is staff-only and its pageviews would
 * pollute the numbers.
 *
 * `afterInteractive` matches MetaPixel and keeps GTM off the critical path.
 * Events pushed before the container finishes loading are not lost — the data
 * layer is a plain array that GTM drains on arrival, and `pushToDataLayer`
 * creates it if it doesn't exist yet.
 */
export default function GoogleTagManager() {
  if (!GTM_ID) return null;

  return (
    <>
      {/* Consent defaults MUST be pushed before the container loads — a tag
          that fires before any signal arrives is treated as granted.
          A plain inline script, not next/script: this renders as the first
          child of <body> and so executes during parse, whereas the GTM loader
          below is afterInteractive and runs later. It lives here rather than in
          its own component so the two can never be installed apart. */}
      <script dangerouslySetInnerHTML={{ __html: CONSENT_DEFAULT_SCRIPT }} />
      {/* The noscript iframe must sit immediately after the opening <body> tag,
          which is why this component is placed first rather than beside the
          other providers. It only does anything for JS-disabled browsers. */}
      <noscript>
        <iframe
          src={`https://www.googletagmanager.com/ns.html?id=${GTM_ID}`}
          height="0"
          width="0"
          style={{ display: "none", visibility: "hidden" }}
          title="Google Tag Manager"
        />
      </noscript>
      <Script id="google-tag-manager" strategy="afterInteractive">
        {`(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
})(window,document,'script','dataLayer','${GTM_ID}');`}
      </Script>
    </>
  );
}
